import {
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useNavigate, useLocation } from "react-router-dom";

import { PermissionGate } from "@/common/components/PermissionGate";
import DialogBox from "@/common/components/popups/DialogBox";
import { useAuth } from "@/common/hooks/useAuth";
import { useToast } from "@/common/hooks/useToast";
import {
  useTestCaseTypes,
  usePriorities,
  useTestCaseStatuses,
  useAllTestDataQuery,
  useRequirements,
  useTeamMembers,
  useReadyTestCases,
  useTestTags,
} from "@/common/services/scenarios";
import {
  useSiteSyncStatus,
  useSiteSyncPages,
} from "@/common/services/site-sync/site-sync.queries";
import { Flex } from "@/compat/radix-themes";
import { Button } from "@/components/ui/button";
import SyncedGeneratorModal from "@/pages/scenarios/all-scenarios/ai/SyncedGeneratorModal";
import CreateTest from "@/pages/scenarios/all-scenarios/CreateTest";

/** Custom event names dispatched globally when shortcuts fire. */
export const SHORTCUT_OPEN_AI_GENERATOR = "app:open-ai-generator";
export const SHORTCUT_OPEN_CREATE_TESTCASE = "app:open-create-testcase";

interface GlobalShortcutsProviderProps {
  children: ReactNode;
}

/**
 * Registers global keyboard shortcuts that work from ANY page in the app
 * (like ⌘K for the command palette — opens an overlay, no page navigation):
 *   ⌘+A / Ctrl+A  → Open AI test case generator modal (overlay)
 *   ⌘+C / Ctrl+C  → Open Create Test Case dialog (overlay)
 */
const GlobalShortcutsProvider = ({
  children,
}: GlobalShortcutsProviderProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { showToast } = useToast();

  const { userPreferenceData, useWorkspaceVersions } = useAuth();
  const versionId = userPreferenceData?.data?.versionId;
  const projectId = userPreferenceData?.data?.projectId;

  // Fetch sync data for AI modal (same hooks as AllScenarios)
  const { data: syncStatus } = useSiteSyncStatus(versionId, undefined);
  const isSynced =
    syncStatus?.status === "synced" && (syncStatus.pageCount ?? 0) > 0;
  const { data: syncPagesData } = useSiteSyncPages(
    syncStatus?.status === "synced" ? syncStatus.syncId : undefined,
  );
  const currentWorkspace = useWorkspaceVersions?.data;
  const syncProjectName =
    currentWorkspace?.workspace?.project?.name || "Application";
  const syncAppUrl =
    syncStatus?.appUrl ||
    currentWorkspace?.workspace?.project?.websiteurl ||
    "";

  // ----- AI Generator Modal state -----
  const [aiModalOpen, setAiModalOpen] = useState(false);
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiOutputMode, setAiOutputMode] = useState<"manual" | "automation">(
    "manual",
  );

  // ----- Create Test Case Dialog state -----
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [isSavingTestCase, setIsSavingTestCase] = useState(false);
  const [isTitleDuplicate, setIsTitleDuplicate] = useState(false);
  const [testCaseType, setTestCaseType] = useState<string>("manual");
  const formRef = useRef<{ submit: (action: string) => void } | null>(null);

  // Fetch metadata for Create Test dialog (only when dialog is open)
  const testCaseTypes = useTestCaseTypes(projectId, createDialogOpen);
  const testCasePriorities = usePriorities(createDialogOpen);
  const testCaseStatuses = useTestCaseStatuses(projectId, createDialogOpen);
  const testData = useAllTestDataQuery(versionId, createDialogOpen);
  const requirements = useRequirements(versionId, createDialogOpen);
  const teamMembers = useTeamMembers(createDialogOpen);
  const testPrerequisite = useReadyTestCases(versionId, createDialogOpen);
  const testTags = useTestTags(createDialogOpen);

  const handleSave = useCallback((action: string) => {
    formRef.current?.submit(action);
  }, []);

  const openAiModal = useCallback(() => {
    if (isSynced) {
      setAiPrompt("");
      setAiOutputMode("manual");
      setAiModalOpen(true);
    } else {
      // Not synced — go to test cases page which has the full sidebar generator
      if (!location.pathname.startsWith("/testcases")) {
        navigate("/testcases/all", {
          state: { shortcutAction: SHORTCUT_OPEN_AI_GENERATOR },
        });
      } else {
        window.dispatchEvent(new CustomEvent(SHORTCUT_OPEN_AI_GENERATOR));
      }
    }
  }, [isSynced, location.pathname, navigate]);

  const openCreateTestCase = useCallback(() => {
    // Always open as overlay — no navigation needed
    setCreateDialogOpen(true);
  }, []);

  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      const isMeta = event.metaKey || event.ctrlKey;
      if (!isMeta) return;

      // event.key can be undefined for synthetic events (e.g. browser autofill),
      // so guard before normalizing to avoid a crash in the global handler.
      const key = event.key?.toLowerCase();
      if (key !== "g" && key !== "n") return;

      const target = event.target as HTMLElement | null;
      if (
        target?.tagName === "INPUT" ||
        target?.tagName === "TEXTAREA" ||
        target?.isContentEditable
      ) {
        return;
      }

      event.preventDefault();

      if (key === "g") {
        openAiModal();
      } else {
        openCreateTestCase();
      }
    };

    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [openAiModal, openCreateTestCase]);

  return (
    <>
      {children}

      {/* Global AI Generator Modal — opens from anywhere via ⌘+A */}
      {isSynced && syncStatus && (
        <SyncedGeneratorModal
          open={aiModalOpen}
          onClose={() => setAiModalOpen(false)}
          prompt={aiPrompt}
          onPromptChange={setAiPrompt}
          handleFileUpload={() => {}}
          syncStatus={syncStatus}
          appUrl={syncAppUrl}
          projectName={syncProjectName}
          pages={syncPagesData?.pages || []}
          outputMode={aiOutputMode}
          onOutputModeChange={setAiOutputMode}
          projectId={projectId}
        />
      )}

      {/* Global Create Test Case Dialog — opens from anywhere via ⌘+C */}
      <DialogBox
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        title="Create Test"
        description="Add details for your new test case."
        footer={
          <Flex gap="3" justify="end">
            <Button
              variant="outline"
              onClick={() => setCreateDialogOpen(false)}
              disabled={isSavingTestCase}
            >
              Cancel
            </Button>
            {testCaseType === "automation" && (
              <PermissionGate resource="TEST_CASES" action="WRITE">
                <Button
                  onClick={() => handleSave("SAVE")}
                  disabled={isTitleDuplicate || isSavingTestCase}
                >
                  Write Test Cases Manually
                </Button>
              </PermissionGate>
            )}
            {testCaseType === "automation" && (
              <PermissionGate resource="TEST_CASES" action="WRITE">
                <Button
                  onClick={() => handleSave("RECORD")}
                  disabled={isTitleDuplicate || isSavingTestCase}
                >
                  Record
                </Button>
              </PermissionGate>
            )}
            {testCaseType !== "automation" && (
              <PermissionGate resource="TEST_CASES" action="WRITE">
                <Button
                  onClick={() => handleSave("SAVE")}
                  disabled={isTitleDuplicate || isSavingTestCase}
                >
                  Save Test Case
                </Button>
              </PermissionGate>
            )}
          </Flex>
        }
      >
        <CreateTest
          ref={formRef}
          onCloseDialog={(data) => {
            setCreateDialogOpen(false);
            if (data) {
              showToast({
                id: "testcase-create-success",
                title: "Test Case Created",
                description: `"${data?.data?.name || "Test case"}" was created successfully.`,
                variant: "success",
                position: "top-right",
                autoDismiss: true,
              });
            }
          }}
          activeFolderId={null}
          testCaseTypes={testCaseTypes?.data?.data}
          testCasePriorities={testCasePriorities?.data?.data}
          testCaseStatuses={testCaseStatuses?.data?.data}
          testData={testData?.data?.data}
          requirements={requirements?.data?.data}
          teamMembers={teamMembers?.data?.data}
          testPrerequisite={testPrerequisite?.data?.data}
          testTags={testTags?.data?.data}
          loading={
            testCaseTypes.isLoading ||
            testCasePriorities.isLoading ||
            testData.isLoading ||
            requirements.isLoading ||
            teamMembers.isLoading
          }
          onSavingChange={setIsSavingTestCase}
          onTitleDuplicateChange={setIsTitleDuplicate}
          onChangeTestCaseType={setTestCaseType}
        />
      </DialogBox>
    </>
  );
};

export default GlobalShortcutsProvider;
